import { type DialogRootProps } from "@ark-ui/react/dialog";
import { JSX } from "react";
import { css } from "styled-system/css";

export interface ModalRenderProps {
  close: () => void;
  isOpen: boolean;
}

export interface BaseModalProps {
  /** A function that returns a JSX.Element to be rendered as the dialog content. */
  renderContent: (renderProps: ModalRenderProps) => JSX.Element;
  /**
   * The dialog's role.
   *
   * Use `alertdialog` when you intend to interrupt a user's workflow to communicate an
   * important message and require a response. Default to `dialog` for all other use cases.
   */
  role?: DialogRootProps["role"];
  /**
   * Whether or not the modal should be closed after one of the following interactions:
   * - The escape key is pressed
   * - Any interaction event (pointer or focus) occurs outside of the modal element
   */
  dismissible?: boolean;
  /** A function to be invoked when the dialog's exit animation completes */
  onExitComplete?: DialogRootProps["onExitComplete"];
  /** An optional ID to apply to the dialog root element */
  id?: string;
  /**
   * Marks content outside the dialog as its own, such as a third-party widget portalled to
   * `<body>`: pressing, focusing or pressing Escape in it keeps the dialog open. Tested against
   * each ancestor of the target, across shadow roots.
   */
  ownsElement?: (element: Element) => boolean;
}

/** Dialog handlers that ignore outside interactions landing in elements the dialog owns. */
export function ownedOutsideHandlers(ownsElement: BaseModalProps["ownsElement"]) {
  if (!ownsElement) return {};
  const owned = (target: EventTarget | null) => {
    for (let node = target instanceof Node ? target : null; node;) {
      if (node instanceof Element && ownsElement(node)) return true;
      node = node instanceof ShadowRoot ? node.host : node.parentNode;
    }
    return false;
  };
  return {
    onInteractOutside: (event: CustomEvent<{ target: EventTarget }>) => {
      if (owned(event.detail.target)) event.preventDefault();
    },
    onEscapeKeyDown: (event: KeyboardEvent) => {
      if (owned(event.target)) event.preventDefault();
    },
  };
}

export const backdropStyles = css({
  zIndex: "dialog",
  position: "fixed",
  inset: "0",
  bg: "dialog.backdrop",
  "&[data-state='open']": {
    animationStyle: "fade-in",
  },
  "&[data-state='closed']": {
    animationStyle: "fade-out",
  },
});

export const contentStyles = css({
  zIndex: "dialog",
  position: "fixed",
  top: "[50%]",
  left: "[50%]",
  transform: "translate(-50%, -50%) scale(0)",
  "&[data-state='open']": {
    animationStyle: "dialog-scale-in",
  },
  "&[data-state='closed']": {
    animationStyle: "dialog-scale-out",
  },
});
